import { CommonModule, NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, ContentChild, input, Input, OnDestroy, signal, TemplateRef, ViewChild } from '@angular/core'
import { SimpleSize } from '@engineering11/ui-lib/e11-ui-config'
import { ITraitDots } from '../core/culture-index.model'
import { CultureIndexChartCore } from './culture-index-chart-core.component'
import { generateRandomTraitDots } from './utils/culture-index-chart.util'

/**
 * Culture Index Chart
 * @example
 * ```html
  <culture-index-chart
    containerClass="e11-ml-auto"
    [traitDots]="traitDots"
    [compareTraitDots]="compareTraitDots"
    [loading]="loading"
  >

    <ng-template #cxChartHeader>
      <p> Chart Header </p>
    </ng-template>

    <ng-template #cxChartFooter>
      <p> Chart Footer </p>
    </ng-template>

  </culture-index-chart>
  ```
 */
@Component({
  selector: 'culture-index-chart',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgClass, CultureIndexChartCore, CommonModule],
  template: `
    <div
      class="e11-w-full e11-aspect-[16/8] {{ containerClass }}"
      [ngClass]="{
        'e11-max-w-[528px]': size === 'md',
        'e11-max-w-[295px]': size === 'sm',
        'e11-animate-pulse': loading
      }"
    >
      <ng-container *ngIf="cxChartHeaderTemplate">
        <ng-container *ngTemplateOutlet="cxChartHeaderTemplate"></ng-container>
      </ng-container>

      <culture-index-chart-core
        #cultureIndexChartCore
        [size]="size"
        [loading]="loading"
        [traitDots]="computedTraitDots()"
        [compareTraitDots]="computedCompareTraitDots()"
        [overrideEuText]="euText"
        [suppressExitAnimation]="suppressExitAnimation"
      ></culture-index-chart-core>

      <ng-container *ngIf="cxChartFooterTemplate">
        <ng-container *ngTemplateOutlet="cxChartFooterTemplate"></ng-container>
      </ng-container>
    </div>
  `,
})
export class CultureIndexChart implements OnDestroy {
  @ViewChild('cultureIndexChartCore') cultureIndexChartCore!: CultureIndexChartCore
  @ContentChild('cxChartHeader', { static: false }) cxChartHeaderTemplate?: TemplateRef<unknown>
  @ContentChild('cxChartFooter', { static: false }) cxChartFooterTemplate?: TemplateRef<unknown>
  @Input() size: Omit<SimpleSize, 'lg'> = 'md'
  @Input() euText?: string
  @Input() suppressExitAnimation?: boolean
  @Input() loadingAnimationDuration: number = 3 * 1000
  @Input() loadingAnimationTotalDuration: number = 21 * 1000

  private _remainingAnimationCount: number = 0

  private _loading = signal(false)
  @Input() set loading(value: boolean) {
    this._loading.set(value)
    if (value) this.startLoadingAnimation()
    else this.stopLoadingAnimation()
  }

  loadingAnimation = input<boolean>(true)

  get loading() {
    return this._loading()
  }

  @Input() containerClass?: string
  traitDots = input<ITraitDots>()
  compareTraitDots = input<ITraitDots>()

  computedTraitDots = computed(() => {
    const isLoading = this.loading
    const isAnimating = this.loadingAnimation()
    const hasTraitDots = !!this.traitDots()
    if (isLoading) {
      if (isAnimating || !hasTraitDots) {
        return this.loadingTraitDots()
      }
      return this.traitDots()
    }
    return this.traitDots()
  })

  computedCompareTraitDots = computed(() => {
    const isLoading = this.loading
    const isAnimating = this.loadingAnimation()
    const hasTraitDots = !!this.traitDots()

    if (isLoading) {
      if (isAnimating || !hasTraitDots) {
        return this.loadingCompareTraitDots()
      }
      return this.compareTraitDots()
    }

    return this.compareTraitDots()
  })

  loadingTraitDots = signal(generateRandomTraitDots())
  loadingCompareTraitDots = signal(generateRandomTraitDots())

  loadingAnimationInterval: NodeJS.Timeout | undefined

  startLoadingAnimation() {
    if (!this.loadingAnimation()) return
    this.calculateRemainAnimationCounts()
    this.loadingAnimationInterval = setInterval(() => {
      this.setRandomPattern()
      this._remainingAnimationCount--
      if (this._remainingAnimationCount < 1) this.stopLoadingAnimation()
    }, this.loadingAnimationDuration)
  }

  calculateRemainAnimationCounts() {
    this._remainingAnimationCount = Math.floor(this.loadingAnimationTotalDuration / this.loadingAnimationDuration)
  }

  stopLoadingAnimation() {
    clearInterval(this.loadingAnimationInterval)
  }

  /**
   * Set the trait to random pattern for loading animation
   */
  setRandomPattern() {
    this.loadingTraitDots.set(generateRandomTraitDots())
    this.loadingCompareTraitDots.set(generateRandomTraitDots())
  }

  ngOnDestroy(): void {
    this.stopLoadingAnimation()
  }
}
